import { useQuery } from "@apollo/client";
import { ReactNode, useState } from "react";
import {
  GetReviewStepQuestionsQuery,
  GetReviewStepQuestionsQueryVariables,
  ReviewQuestionAction,
} from "types/graphql-schema";

import getReviewStepQuestionsQuery from "@apps/programs/graphql/get-review-step-questions-query";
import Dropdown from "@components/dropdown/dropdown";

import { QuestionRow } from "../steps/question-tab-section";
import {
  ReviewQuestionScope,
  findReviewStepTemplate,
} from "./review-question-rows";
import ReviewQuestionsSidebar from "./review-questions-sidebar";
import useChangeReviewQuestion from "./use-change-review-question";

/**
 * A step's talent indicators row, on a published review (TF-1697).
 *
 * The indicators cannot be switched off after Publish, so remove takes the section out of
 * this review instead, and a removed section offers "Show again" in its menu. Edit opens
 * the review's question panel on the talent indicators, where one indicator can be
 * removed. Indicators are never reworded.
 *
 * Without a review, or when the step has no talent indicators, it shows `children`: the
 * row's usual setup.
 */
const ReviewTalentIndicatorsRow = ({
  review,
  title,
  summary,
  editAriaLabel,
  children,
}: {
  review: ReviewQuestionScope | null;
  title: string;
  summary?: string;
  editAriaLabel?: string;
  children: ReactNode;
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { data } = useQuery<
    GetReviewStepQuestionsQuery,
    GetReviewStepQuestionsQueryVariables
  >(getReviewStepQuestionsQuery, {
    variables: { complianceProgramId: review?.complianceProgramId ?? 0 },
    skip: !review,
  });
  const { change, loading, ConfirmationDialog } = useChangeReviewQuestion(
    review ?? { complianceProgramId: 0, assessmentTemplateId: 0 },
  );

  const link =
    (review &&
      findReviewStepTemplate(
        data?.complianceProgram,
        review.assessmentTemplateId,
      )?.questionSetLinks.find(
        ({ isTalentIndicators }) => isTalentIndicators,
      )) ||
    null;

  if (!review || !link) {
    return <>{children}</>;
  }

  return (
    <>
      <ConfirmationDialog />
      {isSidebarOpen && (
        <ReviewQuestionsSidebar
          review={review}
          questionSetId={link.questionSet.id}
          onClose={() => setIsSidebarOpen(false)}
        />
      )}
      <QuestionRow
        title={title}
        summary={link.removed ? "Removed. The answers are kept." : summary}
        added={!link.removed}
        disabled={loading}
        onEdit={() => setIsSidebarOpen(true)}
        onRemove={() =>
          change(
            {
              action: ReviewQuestionAction.RemoveQuestionSet,
              questionSetId: link.questionSet.id,
            },
            {
              successTitle: "Section removed",
              confirmation: {
                title: "Remove this section?",
                message:
                  'People will no longer see this section. Their answers stay saved. To bring the section back, choose "Show again" in its menu.',
              },
            },
          )
        }
        editAriaLabel={editAriaLabel}
        addActions={
          <Dropdown
            align="end"
            disabled={loading}
            aria-label={`${title} menu`}
            options={[
              {
                label: "Show again",
                onClick: () =>
                  change(
                    {
                      action: ReviewQuestionAction.ShowQuestionSet,
                      questionSetId: link.questionSet.id,
                    },
                    { successTitle: "Section shown again" },
                  ),
              },
            ]}
          />
        }
      />
    </>
  );
};

export default ReviewTalentIndicatorsRow;
